import { type ReactNode, useEffect, useState } from 'react';
import { type JSX } from 'react/jsx-runtime';
import {
	StableChoreographerProvider,
	useChoreographerContext,
} from '@atlassian/post-office-context/choreographer';
import { useMessageContext } from '@atlassian/post-office-context/message-context';

type ChoreographerFactory = NonNullable<ReturnType<typeof useChoreographerContext>['factory']>;

export interface ChoreographedModalProps {
	isOpen: boolean;
	shouldChoreograph: boolean;
	onShown?: () => void;
	children: (canShow: boolean) => ReactNode;
}

const SlotClaim = ({
	factory,
	messageId,
	onChange,
}: {
	factory: ChoreographerFactory;
	messageId: string;
	onChange: (hasSlot: boolean) => void;
}): null => {
	// Claims the slot on mount and releases it on unmount.
	const hasSlot = factory.useChoreographedRender({ messageId });

	useEffect(() => {
		onChange(hasSlot);
	}, [hasSlot, onChange]);

	useEffect(() => () => onChange(false), [onChange]);

	return null;
};

const ChoreographedModalInner = ({
	isOpen,
	shouldChoreograph,
	onShown,
	children,
}: ChoreographedModalProps): JSX.Element => {
	const { factory, choreographerEnabled } = useChoreographerContext();
	const { messageTemplateId, messageInstanceId } = useMessageContext();
	const messageId = messageTemplateId ?? messageInstanceId;
	const [hasSlot, setHasSlot] = useState(false);

	// Fall back to showing immediately if the choreographer is disabled or we can't identify the message.
	const isChoreographed = shouldChoreograph && choreographerEnabled && Boolean(messageId);
	const canShow = isOpen && (isChoreographed ? hasSlot : true);

	useEffect(() => {
		if (canShow) {
			onShown?.();
		}
	}, [canShow, onShown]);

	return (
		<>
			{isOpen && isChoreographed && factory && messageId && (
				<SlotClaim factory={factory} messageId={messageId} onChange={setHasSlot} />
			)}
			{children(canShow)}
		</>
	);
};

/**
 * Choreographs only the modal, not the rest of the message. While the modal
 * holds the slot, other choreographed messages (e.g. spotlights) are blocked until it closes.
 */
export const ChoreographedModal = (props: ChoreographedModalProps): JSX.Element => (
	<StableChoreographerProvider choreographer={{ isDefaultEnabled: true }}>
		<ChoreographedModalInner {...props} />
	</StableChoreographerProvider>
);
